/** @import {WithFeatureFlagsProps} from '../FeatureFlags/FeatureFlagsTypes' */
/** @import {CustomerDataContextType } from '../../contexts/CustomerDataProviderTypes' */

import React, {useContext} from 'react';
import './newAccounts.scss';
import {
    allProductTypes,
    getProductByType,
    productTypes
} from '../../utils/productHelpers';

import Loading from '../Loading/Loading';
import NewAccount from './NewAccount';

import {customerDataContext} from '../../contexts/CustomerDataProvider';
import {newAccountsData} from './newAccountsData';

import withFeatureFlags from '../../components/FeatureFlags/withFeatureFlags';
import {existingApplicationAcceptedInSession} from "../../utils/helpers";

function NewAccounts(/** @type {WithFeatureFlagsProps} */ {featureEnabled}) {
        const data = useContext(customerDataContext);

        if (!data || data.loading || !data.data) return <Loading message="We're loading your details..." />;

        // Threshold for blocking further Fixed Rate Cash ISA applications.
        // NOTE: User spec mentioned both 19001.01 and £19,000.01; assuming £19,000.01 (19000.01) as requirement.
        const ISA_ALLOWANCE_BLOCK_THRESHOLD = 19000.01;
        // Now sourced directly from provider (isaAllowance added to query & types)
        const isaUsed = data?.data?.customer?.isaAllowance?.usedAllowance ?? 0;

        const isaLimitBreached = isaUsed >= ISA_ALLOWANCE_BLOCK_THRESHOLD;

        const isSecondaryAccountEnabled = (/** @type {any} */ acc) => {
            if (featureEnabled('SecondaryAccountAppsOffline'))
                return false;

            return (acc.productType === productTypes.EasyAccess && !featureEnabled('SecondaryAccountOFSDisabled'))
                    || (acc.productType === productTypes.Notice && !featureEnabled('SecondaryAccountNSDisabled'))
                    || (acc.productType === productTypes.FixedTerm && !featureEnabled('SecondaryAccountFRSDisabled'))
                    || (acc.productType === productTypes.FixedRateCashISA && !featureEnabled('SecondaryAccountFISADisabled'));
        }

        const mapNewAccountData = (/** @type {any} */ products, /** @type {string} */ issueNumber, /** @type {string} */ rateChangeNotification, /** @type {any} */ rateChanges) => {
        const detail = newAccountsData.find(obj => {
            return obj.productType === products[0].productType;
        });
        if (!detail)
            return null;

        const canAcceptApplication = !existingApplicationAcceptedInSession(products);
        const isProductEnabled = isSecondaryAccountEnabled(products[0]);
        const productDisplayName = detail.productType === productTypes.Notice
            ? `${detail.productName} Issue ${issueNumber}`
            : detail.productName;
        
        // Special handling for FISA - disable if ISA allowance usage breaches threshold
        const isFisaProduct = detail.productType === productTypes.FixedRateCashISA;
        // FISA behaviour: if this is a Fixed Rate Cash ISA, disable applications when
        // the customer's ISA allowance is breached. The FISA description still receives
        // `isaLimitBreached` so it can render the transfer-aware warning copy when needed.
        const fisaEnabled = isFisaProduct ? !isaLimitBreached : true;
        
        return {
            product: productDisplayName,
            disabledMessage: products[0].qualify && canAcceptApplication && !isProductEnabled
                ? (isFisaProduct
                    ? "We're temporarily closed to applications from existing customers, but we'll be open for more soon."
                    : "We're temporarily closed to applications from existing customers, but we'll be open for more soon.")
                : "",
            description: isFisaProduct
                ? detail.descriptionQualify(products, featureEnabled, rateChangeNotification, rateChanges, isaLimitBreached)
                : ((!products[0].qualify || !canAcceptApplication || (isFisaProduct && !fisaEnabled)) && detail.descriptionNotQualify(products[0].productRate, products[0].productAerRate, products[0].productData, featureEnabled)
                    ? detail.descriptionNotQualify(products[0].productRate, products[0].productAerRate, products[0].productData, featureEnabled, rateChangeNotification, rateChanges)
                    : detail.descriptionQualify(products, featureEnabled, rateChangeNotification, rateChanges)),
            enabled: products[0].qualify && canAcceptApplication && isProductEnabled,
            button: {
                label: detail.button.label,
                click: detail.button.click,
                dataAnalytics: detail.button.dataAnalytics,
                // Keep 'Explore' clickable on the dashboard when applications are temporarily paused.
                alwaysEnable: products[0].qualify && canAcceptApplication && !isProductEnabled
            }
        }
    };

    const renderNewAccounts = (/** @type {CustomerDataContextType} */ data) => {
        if (!data || !data.data || !data.data.customer || !data.data.customer.newAccounts)
            return null;
        
        const productsByType = allProductTypes.map(p => getProductByType(data.data?.customer.newAccounts, p))
        
        return productsByType.map(products => {
            if (!products || !Array.isArray(products) || products.length === 0) {
                return null;
            }
            //@ts-expect-error there's a better way to do this
            if (products[0].productType === productTypes.Notice) {
                const nsProduct = getProductByType(data.data?.customer.products, productTypes.Notice);
                //@ts-expect-error there's a better way to do this
                return <NewAccount account={mapNewAccountData(products, nsProduct[0].issueNumber, '', [])} featureEnabled={featureEnabled} key={products[0].productType}/>
                //@ts-expect-error there's a better way to do this
            } else if (products[0].productType === productTypes.EasyAccess) {
                //@ts-expect-error there's a better way to do this
                const ofsProduct = getProductByType(data.data.customer.products, productTypes.EasyAccess);
                //@ts-expect-error there's a better way to do this
                return <NewAccount account={mapNewAccountData(products, ofsProduct[0].issueNumber, ofsProduct[0].rateChangeNotification, ofsProduct[0].rateChanges)} featureEnabled={featureEnabled} key={products[0].productType}/>
            }
            //@ts-expect-error there's a better way to do this
            return <NewAccount account={mapNewAccountData(products, '', '', [])} featureEnabled={featureEnabled} key={products[0].productType}/>
        });
    };

    return (
        <>
            <div className="my-profile my-profile--no-left-padding my-profile--my-account">
                <h1 className="my-profile__title">Choose a new account</h1>
                {renderNewAccounts(data)}
            </div>
            <div>
                <p className="my-profile__aer-info">* AER stands for the Annual Equivalent Rate and illustrates what the
                    interest rate would be if interest was paid and compounded once a year.
                    <br/><br/>The AER is intended to be an indicative rate to help you compare the return on different
                    savings products.
                    <br /><br />
                    ^ Gross p.a. is the rate of interest applied to your account before any tax is deducted.
                </p>
            </div>
        </>
    );
};

export default withFeatureFlags(NewAccounts);